import {
  COLLECTION_FIELDS,
  CONTEXT_LIMITS,
  RELATIONSHIP_LABELS,
  RELATIONSHIP_MODES,
  RELATIONSHIP_TYPES,
  SCALAR_FIELDS,
  SOURCE_MAPPING_KINDS,
  TYPED_SECTIONS,
  contextOptionLabel,
  describeSource,
  describeSourceMapping,
  newRelationship,
  sourceMappingKey,
  relatedComponents,
  relevantTypedSections,
  resolveContext,
  typedFieldLabel,
  typedSection,
  type CollectionKey,
  type ContextLayer,
  type ContextSource,
  type NormalizedBoard,
  type Relationship,
  type RelationshipType,
  type ResolvedValue,
  type SourceMapping,
  type StructuredContext,
} from '@blueprint/domain'
import { useEffect, useMemo, useState } from 'react'
import { Button, Field, IconButton, Select, TextArea, TextInput } from '../../components/controls.js'
import { Disclosure } from '../cards/intent/Disclosure.js'
import { cardLocation } from '../../state/selectors.js'
import styles from './context.module.css'

interface ContextEditorProps {
  /** Context written at this scope only. */
  value: StructuredContext
  onChange: (next: StructuredContext) => void
  /** Containing scopes, least specific first. Empty for the board. */
  parents: readonly ContextLayer[]
  /** Validation messages keyed by path, e.g. `context.technologies.2`. */
  errors?: Record<string, string>
  /** Offered for structural sources, so a user can go and edit where a value comes from. */
  onOpenSource?: ((source: ContextSource) => void) | undefined
}

function splitItems(text: string): string[] {
  return text
    .split('\n')
    .map((item) => item.trim())
    .filter((item) => item.length > 0)
}

/**
 * Edits the structured context one scope owns, and shows what it inherits beside it.
 * Inherited values are never copied into `value`: overriding writes a local value,
 * excluding records an explicit exclusion, and restoring removes either.
 */
export function ContextEditor({ value, onChange, parents, errors = {}, onOpenSource }: ContextEditorProps) {
  const inherited = useMemo(() => resolveContext(parents, null), [parents])
  const effective = useMemo(
    () => resolveContext(parents, { source: { scope: 'card', id: '', title: '' }, context: value }),
    [parents, value],
  )
  const [addedSections, setAddedSections] = useState<string[]>([])
  const sections = relevantTypedSections(effective, addedSections)
  const contextErrors = Object.entries(errors).filter(([key]) => key.startsWith('context.'))

  const set = <K extends keyof StructuredContext>(key: K, next: StructuredContext[K]) =>
    onChange({ ...value, [key]: next })

  const sourceLink = (source: ContextSource) =>
    onOpenSource && source.scope !== 'card' ? (
      <button type="button" className={styles.linkButton} onClick={() => onOpenSource(source)}>
        Open {source.scope} context
      </button>
    ) : null

  /** What a scalar inherits, and whether the local value replaces it. */
  const inheritance = (resolved: ResolvedValue | undefined, local: string, display: (v: string) => string, restore: () => void) => {
    if (!resolved) return undefined
    const overridden = local.trim().length > 0
    return (
      <span className={styles.inherited}>
        {overridden ? 'Overrides' : 'Inherited:'}{' '}
        <span className={`${styles.inheritedValue} ${overridden ? styles.overridden : ''}`}>{display(resolved.value)}</span>
        from {describeSource(resolved.source)}
        {overridden ? (
          <button type="button" className={styles.linkButton} onClick={restore}>
            Restore inherited
          </button>
        ) : null}
        {sourceLink(resolved.source)}
      </span>
    )
  }

  const toggleExclusion = (key: CollectionKey | 'sourceMappings', item: string, exclude: boolean) => {
    const current = value.excluded[key] ?? []
    const next = exclude
      ? [...current, item]
      : current.filter((existing) => existing.toLowerCase() !== item.toLowerCase())
    const excluded = { ...value.excluded, [key]: next }
    if (next.length === 0) delete excluded[key]
    set('excluded', excluded)
  }

  const setTyped = (sectionKey: string, fieldKey: string, fieldValue: string) => {
    const section = { ...(value.typed[sectionKey] ?? {}), [fieldKey]: fieldValue }
    if (fieldValue.length === 0) delete section[fieldKey]
    const typed = { ...value.typed, [sectionKey]: section }
    if (Object.keys(section).length === 0) delete typed[sectionKey]
    set('typed', typed)
  }

  const offerable = TYPED_SECTIONS.filter((section) => !sections.includes(section.key))

  return (
    <div className={styles.stack}>
      {contextErrors.length > 0 ? (
        <ul className={styles.errors} role="alert">
          {contextErrors.map(([key, message]) => (
            <li key={key}>
              {key.replace(/^context\./, '')}: {message}
            </li>
          ))}
        </ul>
      ) : null}

      <div className={styles.grid}>
        {SCALAR_FIELDS.map((field) => {
          const local = value[field.key]
          const display = field.options ? contextOptionLabel : (v: string) => v
          const hint = inheritance(inherited.scalars[field.key], local, display, () => set(field.key, ''))
          return (
            <div key={field.key} className={field.key === 'responsibility' ? styles.full : undefined}>
              <Field label={field.label} hint={hint}>
                {(id) =>
                  field.options ? (
                    <Select id={id} value={local} onChange={(event) => set(field.key, event.target.value as never)}>
                      <option value="">{inherited.scalars[field.key] ? 'Inherit' : 'Not set'}</option>
                      {field.options.map((option) => (
                        <option key={option} value={option}>
                          {contextOptionLabel(option)}
                        </option>
                      ))}
                    </Select>
                  ) : field.key === 'responsibility' ? (
                    <TextArea
                      id={id}
                      value={local}
                      maxLength={field.max}
                      style={{ minHeight: 60 }}
                      placeholder={inherited.scalars.responsibility ? 'Inherit' : 'What this component owns'}
                      onChange={(event) => set(field.key, event.target.value)}
                    />
                  ) : (
                    <TextInput
                      id={id}
                      value={local}
                      maxLength={field.max}
                      placeholder={inherited.scalars[field.key] ? 'Inherit' : undefined}
                      onChange={(event) => set(field.key, event.target.value)}
                    />
                  )
                }
              </Field>
            </div>
          )
        })}
      </div>

      {COLLECTION_FIELDS.map((field) => {
        const fromParents = inherited.collections[field.key]
        const excludedHere = new Set((value.excluded[field.key] ?? []).map((item) => item.toLowerCase()))
        const stale = (value.excluded[field.key] ?? []).filter(
          (item) => !fromParents.some((parent) => parent.value.toLowerCase() === item.toLowerCase()),
        )
        const localCount = value[field.key].length
        const total = effective.collections[field.key].length
        return (
          <Disclosure
            key={field.key}
            label={field.label}
            defaultOpen={localCount > 0 || fromParents.length > 0}
            filled={total > 0 ? `${total} item${total === 1 ? '' : 's'}` : undefined}
          >
            {fromParents.length > 0 || stale.length > 0 ? (
              <ul className={styles.items} aria-label={`Inherited ${field.label.toLowerCase()}`}>
                {fromParents.map((item) => {
                  const isExcluded = excludedHere.has(item.value.toLowerCase())
                  return (
                    <li key={item.value} className={`${styles.item} ${isExcluded ? styles.itemExcluded : ''}`}>
                      <span className={styles.itemValue}>{item.value}</span>
                      <span className={styles.itemSource}>
                        {isExcluded ? 'Excluded here · ' : ''}from {describeSource(item.source)}
                      </span>
                      <button
                        type="button"
                        className={styles.linkButton}
                        onClick={() => toggleExclusion(field.key, item.value, !isExcluded)}
                      >
                        {isExcluded ? 'Restore' : 'Exclude'}
                      </button>
                    </li>
                  )
                })}
                {stale.map((item) => (
                  <li key={`stale-${item}`} className={`${styles.item} ${styles.itemExcluded}`}>
                    <span className={styles.itemValue}>{item}</span>
                    <span className={styles.itemSource}>Excluded here · no longer inherited</span>
                    <button type="button" className={styles.linkButton} onClick={() => toggleExclusion(field.key, item, false)}>
                      Remove exclusion
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
            <ItemsField
              label={`${field.label} at this scope`}
              items={value[field.key]}
              onChange={(items) => set(field.key, items)}
            />
          </Disclosure>
        )
      })}

      <SourceMappingsField
        value={value.sourceMappings}
        excluded={value.excluded.sourceMappings ?? []}
        inherited={inherited.sourceMappings}
        effectiveCount={effective.sourceMappings.length}
        onChange={(sourceMappings) => set('sourceMappings', sourceMappings)}
        onToggleExclusion={(key, exclude) => toggleExclusion('sourceMappings', key, exclude)}
      />

      {sections.map((sectionKey) => {
        const definition = typedSection(sectionKey)
        const localSection = value.typed[sectionKey] ?? {}
        const inheritedSection = inherited.typed[sectionKey] ?? {}
        const fieldKeys = [
          ...(definition?.fields.map((field) => field.key) ?? []),
          ...Object.keys(localSection),
          ...Object.keys(inheritedSection),
        ].filter((key, index, all) => all.indexOf(key) === index)
        const count = Object.keys(effective.typed[sectionKey] ?? {}).length
        return (
          <Disclosure
            key={sectionKey}
            label={`${definition?.label ?? sectionKey} context`}
            defaultOpen
            filled={count > 0 ? `${count} set` : undefined}
          >
            <div className={styles.grid}>
              {fieldKeys.map((fieldKey) => {
                const local = localSection[fieldKey] ?? ''
                return (
                  <Field
                    key={fieldKey}
                    label={typedFieldLabel(sectionKey, fieldKey)}
                    hint={inheritance(inheritedSection[fieldKey], local, (v) => v, () => setTyped(sectionKey, fieldKey, ''))}
                  >
                    {(id) => (
                      <TextInput
                        id={id}
                        value={local}
                        maxLength={CONTEXT_LIMITS.typedValue}
                        placeholder={inheritedSection[fieldKey] ? 'Inherit' : undefined}
                        onChange={(event) => setTyped(sectionKey, fieldKey, event.target.value)}
                      />
                    )}
                  </Field>
                )
              })}
            </div>
          </Disclosure>
        )
      })}

      {offerable.length > 0 ? (
        <Field label="Type-specific context" hint="Sections for the component type appear on their own; add others here.">
          {(id) => (
            <Select
              id={id}
              value=""
              onChange={(event) => {
                const key = event.target.value
                if (key) setAddedSections((current) => [...current, key])
              }}
            >
              <option value="">Add a section…</option>
              {offerable.map((section) => (
                <option key={section.key} value={section.key}>
                  {section.label}
                </option>
              ))}
            </Select>
          )}
        </Field>
      ) : null}
    </div>
  )
}

const EMPTY_MAPPING: SourceMapping = { repository: '', path: '', kind: '', ref: '' }

interface SourceMappingsFieldProps {
  value: SourceMapping[]
  /** Keys this scope excludes. */
  excluded: string[]
  inherited: ReturnType<typeof resolveContext>['sourceMappings']
  effectiveCount: number
  onChange: (next: SourceMapping[]) => void
  onToggleExclusion: (key: string, exclude: boolean) => void
}

/** Structured mappings: inherited ones to exclude or restore, and this scope's own rows. */
function SourceMappingsField({ value, excluded, inherited, effectiveCount, onChange, onToggleExclusion }: SourceMappingsFieldProps) {
  const excludedHere = new Set(excluded.map((key) => key.toLowerCase()))
  const stale = excluded.filter((key) => !inherited.some((item) => sourceMappingKey(item.value) === key.toLowerCase()))
  const update = (index: number, patch: Partial<SourceMapping>) =>
    onChange(value.map((mapping, at) => (at === index ? { ...mapping, ...patch } : mapping)))
  return (
    <Disclosure
      label="Source mappings"
      defaultOpen={value.length > 0 || inherited.length > 0}
      filled={effectiveCount > 0 ? `${effectiveCount} mapping${effectiveCount === 1 ? '' : 's'}` : undefined}
    >
      {inherited.length > 0 || stale.length > 0 ? (
        <ul className={styles.items} aria-label="Inherited source mappings">
          {inherited.map((item) => {
            const key = sourceMappingKey(item.value)
            const isExcluded = excludedHere.has(key)
            return (
              <li key={key} className={`${styles.item} ${isExcluded ? styles.itemExcluded : ''}`}>
                <span className={styles.itemValue}>{describeSourceMapping(item.value)}</span>
                <span className={styles.itemSource}>
                  {isExcluded ? 'Excluded here · ' : ''}from {describeSource(item.source)}
                </span>
                <button type="button" className={styles.linkButton} onClick={() => onToggleExclusion(key, !isExcluded)}>
                  {isExcluded ? 'Restore' : 'Exclude'}
                </button>
              </li>
            )
          })}
          {stale.map((key) => (
            <li key={`stale-${key}`} className={`${styles.item} ${styles.itemExcluded}`}>
              <span className={styles.itemValue}>{key.replace(/^\|/, '')}</span>
              <span className={styles.itemSource}>Excluded here · no longer inherited</span>
              <button type="button" className={styles.linkButton} onClick={() => onToggleExclusion(key, false)}>
                Remove exclusion
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <div className={styles.stack}>
        {value.map((mapping, index) => (
          <div key={index} className={styles.mapping}>
            <TextInput
              aria-label="Repository"
              placeholder="Repository (optional)"
              value={mapping.repository}
              onChange={(event) => update(index, { repository: event.target.value })}
            />
            <TextInput
              aria-label="Path"
              placeholder="Path, e.g. services/auth"
              value={mapping.path}
              onChange={(event) => update(index, { path: event.target.value })}
            />
            <Select
              aria-label="Kind"
              value={mapping.kind}
              onChange={(event) => update(index, { kind: event.target.value as SourceMapping['kind'] })}
            >
              <option value="">Kind</option>
              {SOURCE_MAPPING_KINDS.map((kind) => (
                <option key={kind} value={kind}>
                  {kind}
                </option>
              ))}
            </Select>
            <TextInput
              aria-label="Ref"
              placeholder="Branch, tag or commit"
              value={mapping.ref}
              onChange={(event) => update(index, { ref: event.target.value })}
            />
            <IconButton danger aria-label="Remove mapping" onClick={() => onChange(value.filter((_, at) => at !== index))}>
              &#10005;
            </IconButton>
          </div>
        ))}
        <div>
          <Button
            disabled={value.length >= CONTEXT_LIMITS.itemCount}
            onClick={() => onChange([...value, { ...EMPTY_MAPPING }])}
          >
            Add mapping
          </Button>
        </div>
      </div>
    </Disclosure>
  )
}

/**
 * One item per line. The raw text is kept while typing, so a trailing newline survives
 * until the next item is written; the parsed list is what reaches the draft.
 */
function ItemsField({ label, items, onChange }: { label: string; items: string[]; onChange: (items: string[]) => void }) {
  const [text, setText] = useState(items.join('\n'))
  useEffect(() => {
    if (JSON.stringify(splitItems(text)) !== JSON.stringify(items)) setText(items.join('\n'))
    // Only an outside change to the list should rewrite what the user is typing.
  }, [items])
  return (
    <Field label={label} hint="One per line.">
      {(id) => (
        <TextArea
          id={id}
          value={text}
          style={{ minHeight: 60 }}
          onChange={(event) => {
            setText(event.target.value)
            onChange(splitItems(event.target.value))
          }}
        />
      )}
    </Field>
  )
}

interface RelationshipsEditorProps {
  relationships: readonly Relationship[]
  onChange: (next: Relationship[]) => void
  entities: NormalizedBoard
  /** Null while creating a card: it has no incoming relationships yet. */
  cardId: string | null
}

/** Typed edges from this card, plus a read-only list of the edges that point at it. */
export function RelationshipsEditor({ relationships, onChange, entities, cardId }: RelationshipsEditorProps) {
  const targets = useMemo(
    () =>
      Object.values(entities.cards)
        .filter((card) => card.id !== cardId)
        .map((card) => {
          const location = cardLocation(entities, card)
          return { id: card.id, label: `${card.title} — ${location.column} / ${location.row}` }
        })
        .sort((a, b) => a.label.localeCompare(b.label)),
    [entities, cardId],
  )
  const incoming = useMemo(
    () => relatedComponents(entities, cardId, []).filter((item) => item.direction === 'incoming'),
    [entities, cardId],
  )

  return (
    <div className={styles.stack}>
      <RelationshipList relationships={relationships} onChange={onChange} targets={targets} noun="card" />
      {incoming.length > 0 ? (
        <div>
          <p className={styles.sectionTitle}>Pointing at this card</p>
          <ul className={styles.items}>
            {incoming.map((item) => (
              <li key={`${item.otherId}-${item.relationship.id}`} className={styles.item}>
                <span>
                  “{item.otherTitle}” {RELATIONSHIP_LABELS[item.relationship.type].toLowerCase()} this card
                </span>
                <span className={styles.itemSource}>Edit on that card</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  )
}

/**
 * Typed relationships to other items, each picked from `targets` — the cards on a board,
 * or the nodes of a mindmap or workflow.
 */
export function RelationshipList({
  relationships,
  onChange,
  targets,
  noun,
  readOnly = false,
}: {
  relationships: readonly Relationship[]
  onChange: (next: Relationship[]) => void
  targets: readonly { id: string; label: string }[]
  /** What a target is called: "card" or "node". */
  noun: string
  readOnly?: boolean
}) {
  const update = (id: string, patch: Partial<Relationship>) =>
    onChange(relationships.map((item) => (item.id === id ? { ...item, ...patch } : item)))

  return (
    <div className={styles.stack}>
      {relationships.map((relationship) => {
        const known = targets.some((target) => target.id === relationship.targetId)
        return (
          <div key={relationship.id} className={styles.relationship}>
            <Select
              aria-label="Relationship type"
              value={relationship.type}
              disabled={readOnly}
              onChange={(event) => update(relationship.id, { type: event.target.value as RelationshipType })}
            >
              {RELATIONSHIP_TYPES.map((type) => (
                <option key={type} value={type}>
                  {RELATIONSHIP_LABELS[type]}
                </option>
              ))}
            </Select>
            <Select
              aria-label={`Related ${noun}`}
              value={relationship.targetId}
              disabled={readOnly}
              onChange={(event) => update(relationship.id, { targetId: event.target.value })}
            >
              {known ? null : <option value={relationship.targetId}>({noun} no longer on this board)</option>}
              {targets.map((target) => (
                <option key={target.id} value={target.id}>
                  {target.label}
                </option>
              ))}
            </Select>
            {readOnly ? null : (
              <IconButton
                danger
                aria-label="Remove relationship"
                onClick={() => onChange(relationships.filter((item) => item.id !== relationship.id))}
              >
                &#10005;
              </IconButton>
            )}
            <div className={styles.relationshipDetails}>
              <TextInput
                aria-label="Relationship description"
                placeholder="Description"
                value={relationship.description}
                readOnly={readOnly}
                maxLength={CONTEXT_LIMITS.relationshipText}
                onChange={(event) => update(relationship.id, { description: event.target.value })}
              />
              <TextInput
                aria-label="Protocol"
                placeholder="Protocol"
                value={relationship.protocol}
                readOnly={readOnly}
                maxLength={CONTEXT_LIMITS.scalar}
                onChange={(event) => update(relationship.id, { protocol: event.target.value })}
              />
              <TextInput
                aria-label="Contract"
                placeholder="Message / data contract"
                value={relationship.contract}
                readOnly={readOnly}
                maxLength={CONTEXT_LIMITS.relationshipText}
                onChange={(event) => update(relationship.id, { contract: event.target.value })}
              />
              <Select
                aria-label="Interaction"
                value={relationship.mode}
                disabled={readOnly}
                onChange={(event) => update(relationship.id, { mode: event.target.value as Relationship['mode'] })}
              >
                <option value="">Sync or async: not set</option>
                {RELATIONSHIP_MODES.map((mode) => (
                  <option key={mode} value={mode}>
                    {mode === 'sync' ? 'Synchronous' : 'Asynchronous'}
                  </option>
                ))}
              </Select>
            </div>
          </div>
        )
      })}
      {readOnly ? null : (
        <div>
          <Button
            disabled={targets.length === 0 || relationships.length >= CONTEXT_LIMITS.relationshipCount}
            onClick={() => onChange([...relationships, newRelationship(targets[0]!.id)])}
          >
            Add relationship
          </Button>
        </div>
      )}
    </div>
  )
}
